import React, { useState, useEffect } from "react";
import {
  Form,
  Input,
  Button,
  Select,
  Spin,
  message,
  Card,
  Row,
  Col,
  Modal,
  DatePicker,
  Switch,
  Table,
  Typography,
} from "antd";
import api from "../../api";
import { Navigate, useNavigate } from "react-router-dom";
import { DeleteOutlined } from "@ant-design/icons";

const { Option } = Select;
const { TextArea } = Input;
const { Text } = Typography;

const ManualOrderPage = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [loadingAddresses, setLoadingAddresses] = useState(false);
  const [isUserModalVisible, setIsUserModalVisible] = useState(false);
  const [isAddressModalVisible, setIsAddressModalVisible] = useState(false);
  const [carriers, setCarriers] = useState([]);
  const [loadingCarriers, setLoadingCarriers] = useState(false);
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isProductModalVisible, setIsProductModalVisible] = useState(false);
  const [selectedProductsList, setSelectedProductsList] = useState([]);
  const [productForm] = Form.useForm();
  const navigate = useNavigate();

  // Brand, seller, guarantee related states
  const [brands, setBrands] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [guarantees, setGuarantees] = useState([]);
  const [productPrices, setProductPrices] = useState({
    price: null,
    off_price: null,
  });

  const [loadingProductDetails, setLoadingProductDetails] = useState(false);

  const handleOpenProductModal = () => {
    setIsProductModalVisible(true);
    productForm.resetFields();
    setSelectedProduct(null);
    setBrands([]);
    setSellers([]);
    setGuarantees([]);
    setProductPrices({ price: null, off_price: null });
  };

  const calculateTotalPrice = () => {
    return selectedProductsList.reduce((total, product) => {
      return total + (product.price || 0) * (product.count || 0);
    }, 0);
  };

  const handleSearchUser = async (value) => {
    if (value) {
      setSearchingUsers(true);
      try {
        const response = await api.get(`/panel/users?search=${value}`);
        setUsers(response.data.data || []);
      } catch (error) {
        message.error("خطا در دریافت اطلاعات مشتریان");
        console.error("خطا در جستجوی مشتریان:", error);
      } finally {
        setSearchingUsers(false);
      }
    }
  };

  const fetchInitialUsers = async () => {
    setSearchingUsers(true);
    try {
      const response = await api.get(`/panel/users`);
      setUsers(response.data.data || []);
    } catch (error) {
      message.error("خطا در دریافت اطلاعات مشتریان");
      console.error("خطا در دریافت مشتریان:", error);
    } finally {
      setSearchingUsers(false);
    }
  };

  // هنگامی که Select باز می‌شود
  const handleDropdownVisibleChange = (open) => {
    if (open && users.length === 0) {
      fetchInitialUsers();
    }
  };

  const fetchCarriers = async (searchQuery = "") => {
    setLoadingCarriers(true);
    try {
      const response = await api.get(`/panel/carrier?search=${searchQuery}`);
      setCarriers(response.data.data || []);
    } catch (error) {
      message.error("خطا در دریافت اطلاعات حامل‌ها");
      console.error("خطا در دریافت حامل‌ها:", error);
    } finally {
      setLoadingCarriers(false);
    }
  };

  const fetchProducts = async (searchQuery = "") => {
    setLoading(true);
    try {
      const response = await api.get(`/panel/product?search=${searchQuery}`);
      setProducts(response.data.data || []);
    } catch (error) {
      message.error("خطا در دریافت اطلاعات محصولات");
      console.error("خطا در دریافت محصولات:", error);
    } finally {
      setLoading(false);
    }
  };

  // فراخوانی API برای دریافت اطلاعات مینی محصول
  const fetchProductMiniDetails = async (productId) => {
    setLoadingProductDetails(true);
    try {
      const response = await api.post('/panel/product/mini-show', {
        post_id: productId
      });
      
      if (response.data && response.data.data) {
        const productData = response.data.data;
        setSelectedProduct({
          ...productData,
          id: productId
        });
        
        // دریافت برندها از پاسخ API
        if (productData.brands) {
          setBrands(productData.brands);
        }
        
        // دریافت فروشنده‌ها و گارانتی‌ها از پاسخ API
        if (productData.sellers) {
          setSellers(productData.sellers);
        }
        if (productData.guarantees) {
          setGuarantees(productData.guarantees);
        }
        
        // دریافت قیمت و تعداد از پاسخ API
        if (productData.price && productData.count && productData.vipCount) {
          setProductPrices({
            price: productData.price,
            off_price: productData.off_price
          });
          productForm.setFieldsValue({
            price: productData.off_price || productData.price,
            count: productData.count,
          });
        }
        
        // پاک کردن فیلدهای وابسته
        productForm.setFieldsValue({
          brand_id: undefined,
          seller_id: undefined,
          guarantee_id: undefined,
        });
      }
    } catch (error) {
      message.error("خطا در دریافت جزئیات محصول");
      console.error("خطا در دریافت جزئیات محصول:", error);
    } finally {
      setLoadingProductDetails(false);
    }
  };

  // انتخاب برند و دریافت فروشنده‌ها
  const handleBrandChange = async (brandId) => {
    setLoadingProductDetails(true);
    try {
      const response = await api.post('/panel/product/mini-show', {
        post_id: selectedProduct.id,
        brand_id: brandId
      });
      
      if (response.data && response.data.data) {
        const { sellers, guarantees, price, off_price, count, vipCount } = response.data.data;
        
        // به‌روزرسانی فروشنده‌ها و گارانتی‌ها
        setSellers(sellers || []);
        setGuarantees(guarantees || []);
        
        // به‌روزرسانی قیمت و تعداد
        setProductPrices({ price, off_price });
        productForm.setFieldsValue({
          price: off_price || price,
          count: count,
        });
        
        // به‌روزرسانی تعداد در انبار ویژه
        setSelectedProduct((prev) => ({
          ...prev,
          count: count,
        }));
      }
    } catch (error) {
      message.error("خطا در دریافت اطلاعات فروشنده‌ها");
      console.error("خطا در دریافت فروشنده‌ها:", error);
    } finally {
      setLoadingProductDetails(false);
    }
  };

  // انتخاب فروشنده و دریافت گارانتی‌ها
  const handleSellerChange = async (sellerId) => {
    setLoadingProductDetails(true);
    try {
      const response = await api.post("/panel/product/mini-show", {
        post_id: selectedProduct.id,
        brand_id: productForm.getFieldValue("brand_id"),
        seller_id: sellerId,
      });

      if (response.data && response.data.data) {
        const { guarantees, price, off_price, count, vipCount } =
          response.data.data;

        // به‌روزرسانی گارانتی‌ها
        setGuarantees(guarantees || []);

        // به‌روزرسانی قیمت و تعداد
        setProductPrices({ price, off_price });
        productForm.setFieldsValue({
          price: off_price || price,
          count: count,
        });

        // به‌روزرسانی تعداد در انبار ویژه
        setSelectedProduct((prev) => ({
          ...prev,
          vipCount: vipCount,
        }));
      }
    } catch (error) {
      message.error("خطا در دریافت اطلاعات گارانتی‌ها");
      console.error("خطا در دریافت گارانتی‌ها:", error);
    } finally {
      setLoadingProductDetails(false);
    }
  };

  // انتخاب گارانتی و دریافت قیمت‌ها
  const handleGuaranteeChange = async (guaranteeId) => {
    setLoadingProductDetails(true);
    try {
      const response = await api.post("/panel/product/mini-show", {
        post_id: selectedProduct.id,
        brand_id: productForm.getFieldValue("brand_id"),
        seller_id: productForm.getFieldValue("seller_id"),
        guarantee_id: guaranteeId,
      });

      if (response.data && response.data.data) {
        const { price, off_price, count, vipCount } = response.data.data;

        // به‌روزرسانی قیمت و تعداد
        setProductPrices({ price, off_price });
        productForm.setFieldsValue({
          price: off_price || price,
          count: count,
        });

        // به‌روزرسانی تعداد در انبار ویژه
        setSelectedProduct((prev) => ({
          ...prev,
          count: count,
        }));
      }
    } catch (error) {
      message.error("خطا در دریافت اطلاعات قیمت");
      console.error("خطا در دریافت قیمت:", error);
    } finally {
      setLoadingProductDetails(false);
    }
  };

  const fetchProductDetails = async (productId) => {
    setLoading(true);
    try {
      const response = await api.get(`/panel/product/${productId}`);
      setSelectedProduct(response.data.data);
      form.setFieldsValue({
        seller_id: "",
        brand_id: "",
        guarantee_id: "",
        chassi_id: "",
      });

      // فراخوانی API مینی برای دریافت برندها
      fetchProductMiniDetails(productId);
    } catch (error) {
      message.error("خطا در دریافت جزئیات محصول");
      console.error("خطا در دریافت جزئیات محصول:", error);
    } finally {
      setLoading(false);
    }
  };

  // اضافه کردن محصول به لیست
  const handleAddProduct = (values) => {
    const newProduct = {
      ...selectedProduct,
      seller_id: values.seller_id || null, // اگر فروشنده وجود نداشت، null باشد
      brand_id: values.brand_id,
      guarantee_id: values.guarantee_id || null, // اگر گارانتی وجود نداشت، null باشد
      chassi_id: values.chassi_id,
      price: values.price,
      original_price: productPrices.price,
      count: values.count,
      sellers: sellers, // اضافه کردن لیست فروشنده‌ها
      guarantees: guarantees, // اضافه کردن لیست گارانتی‌ها
    };
    setSelectedProductsList([...selectedProductsList, newProduct]);
    setIsProductModalVisible(false);
    productForm.resetFields();
    setSelectedProduct(null);
    setBrands([]);
    setSellers([]);
    setGuarantees([]);
    setProductPrices({ price: null, off_price: null });
  };

  // حذف محصول از لیست
  const handleRemoveProduct = (productId) => {
    setSelectedProductsList(
      selectedProductsList.filter((product) => product.id !== productId)
    );
  };

  // ایجاد کاربر جدید
  const handleCreateUser = async (values) => {
    setLoading(true);
    try {
      const response = await api.post("/panel/users", values);
      message.success("مشتری با موفقیت ایجاد شد.");
      setSelectedUserId(response.data.data.id);
      fetchUserAddresses(response.data.data.id);
      setIsUserModalVisible(false);
    } catch (error) {
      message.error("خطا در ایجاد مشتری");
      console.error("خطا در ایجاد مشتری:", error);
    } finally {
      setLoading(false);
    }
  };

  // دریافت آدرس‌های کاربر
  const fetchUserAddresses = async (userId) => {
    setLoadingAddresses(true);
    try {
      const response = await api.get(
        `/user/address/getAllUserAddresses/${userId}`
      );
      setAddresses(response.data.data || []);
    } catch (error) {
      message.error("خطا در دریافت آدرس‌های مشتری");
      console.error("خطا در دریافت آدرس‌ها:", error);
    } finally {
      setLoadingAddresses(false);
    }
  };

  // ایجاد آدرس جدید برای کاربر
  const handleCreateAddress = async (values) => {
    setLoading(true);
    try {
      const response = await api.post("/user/address/createForUser", {
        ...values,
        user_id: selectedUserId,
      });
      message.success("آدرس با موفقیت ایجاد شد.");
      fetchUserAddresses(selectedUserId);
      setIsAddressModalVisible(false);
    } catch (error) {
      message.error("خطا در ایجاد آدرس");
      console.error("خطا در ایجاد آدرس:", error);
    } finally {
      setLoading(false);
    }
  };

  // انتخاب کاربر
  const handleSelectUser = (userId) => {
    setSelectedUserId(userId);
    fetchUserAddresses(userId);
  };

  // ارسال فرم
  const onFinish = async (values) => {
    setLoading(true);
    try {
      const formData = new FormData();

      // افزودن فیلدهای مورد نیاز به formData
      formData.append("user_id", selectedUserId);
      formData.append("address_id", values.address);
      formData.append("discount_id", values.discount_id || "");
      formData.append("tracking_code", values.tracking_code || "");
      formData.append("payment_method", values.payment_method);
      formData.append("payment_status", values.payment_status);
      formData.append("deliver_status", values.deliver_status);
      formData.append("price", values.price);
      formData.append("time", values.time);
      formData.append("carrier_id", values.carrier_id);
      formData.append(
        "send_payment_link",
        values.send_payment_link ? "1" : "0"
      );

      // افزودن محصولات انتخاب شده
      selectedProductsList.forEach((product, index) => {
        formData.append(`products[${index}][id]`, product.id);
        formData.append(`products[${index}][seller_id]`, product.seller_id);
        formData.append(`products[${index}][brand_id]`, product.brand_id);
        formData.append(
          `products[${index}][guarantee_id]`,
          product.guarantee_id || ""
        );
        formData.append(
          `products[${index}][chassi_id]`,
          product.chassi_id || ""
        );
        formData.append(`products[${index}][price]`, product.price);
        formData.append(`products[${index}][count]`, product.count);
      });

      // ارسال درخواست به بک‌اند
      const response = await api.post("/panel/order", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      message.success("سفارش با موفقیت ایجاد شد.");
      form.resetFields();
      setSelectedProductsList([]);
      navigate("/orders");
    } catch (error) {
      message.error("خطا در ارسال اطلاعات.");
      console.error("خطا در ایجاد سفارش:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCarriers();
    fetchProducts();
  }, []);

  // ستون‌های جدول محصولات انتخاب شده
  const productColumns = [
    {
      title: "محصول",
      dataIndex: "title",
      key: "title",
    },
    {
      title: "فروشنده",
      dataIndex: "seller_id",
      key: "seller_id",
      render: (sellerId, record) => {
        if (sellerId && record.sellers) {
          const seller = record.sellers.find(s => s.id === sellerId);
          return seller ? seller.name : "فروشنده ناشناخته";
        }
        return ""; // اگر فروشنده وجود نداشت، خالی نمایش داده شود
      }
    },
    {
      title: "برند",
      dataIndex: "brand_id",
      key: "brand_id",
      render: (brandId, record) => {
        // اگر از API جدید استفاده می‌شود
        if (record.brands) {
          const brand = record.brands.find((b) => b.id === brandId);
          return brand ? brand.name : "برند ناشناخته";
        }
        // سازگاری با کد قبلی
        return (
          record.brand?.find((brand) => brand.id === brandId)?.name ||
          "برند ناشناخته"
        );
      },
    },
    {
      title: "گارانتی",
      dataIndex: "guarantee_id",
      key: "guarantee_id",
      render: (guaranteeId, record) => {
        if (guaranteeId && record.guarantees) {
          const guarantee = record.guarantees.find(g => g.id === guaranteeId);
          return guarantee ? guarantee.name : "";
        }
        return ""; // اگر گارانتی وجود نداشت، خالی نمایش داده شود
      }
    },
    {
      title: "شماره شاسی",
      dataIndex: "chassi_id",
      key: "chassi_id",
    },
    {
      title: "قیمت",
      dataIndex: "price",
      key: "price",
    },
    {
      title: "تعداد",
      dataIndex: "count",
      key: "count",
    },
    {
      title: "عملیات",
      key: "actions",
      render: (_, record) => (
        <Button
          type="link"
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleRemoveProduct(record.id)}
        ></Button>
      ),
    },
  ];

  return (
    <Card>
      <h2 className="mb-10 text-xl">ایجاد سفارش دستی</h2>
      <Spin spinning={loading}>
        {/* بخش انتخاب کاربر */}
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Row gutter={16} align="middle">
            <Col span={18}>
              <Form.Item
                name="user"
                label="انتخاب مشتری"
                rules={[
                  {
                    required: true,
                    message: "لطفاً مشتری را انتخاب کنید.",
                  },
                ]}
              >
                <Select
                  showSearch
                  placeholder="جستجو و انتخاب مشتری"
                  filterOption={false}
                  onSearch={handleSearchUser}
                  notFoundContent={
                    searchingUsers ? <Spin size="small" /> : "نتیجه‌ای یافت نشد"
                  }
                  loading={searchingUsers}
                  onSelect={handleSelectUser}
                  onDropdownVisibleChange={handleDropdownVisibleChange}
                >
                  {users.map((user) => (
                    <Option key={user.id} value={user.id}>
                      {user.name} - {user.number}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Button
                type="dashed"
                onClick={() => setIsUserModalVisible(true)}
                style={{ marginTop: "7px" }}
              >
                ایجاد مشتری جدید
              </Button>
            </Col>
          </Row>

          {/* بخش انتخاب آدرس */}
          {selectedUserId && (
            <Row gutter={16} align="middle">
              <Col span={18}>
                <Form.Item
                  name="address"
                  label="انتخاب آدرس"
                  rules={[
                    {
                      required: true,
                      message: "لطفاً آدرس را انتخاب کنید.",
                    },
                  ]}
                >
                  <Select placeholder="انتخاب آدرس" loading={loadingAddresses}>
                    {addresses.map((address) => (
                      <Option key={address.id} value={address.id}>
                        {address.address}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={6}>
                <Button
                  type="dashed"
                  onClick={() => setIsAddressModalVisible(true)}
                  style={{ marginTop: "7px" }}
                >
                  افزودن آدرس جدید
                </Button>
              </Col>
            </Row>
          )}

          {/* فیلدهای جدید */}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="time"
                label="زمان"
                rules={[
                  {
                    required: true,
                    message: "لطفاً زمان را انتخاب کنید.",
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="tracking_code"
                label="کد رهگیری"
                rules={[
                  {
                    required: true,
                    message: "لطفاً کد رهگیری را انتخاب کنید.",
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="payment_method"
                label="روش پرداخت"
                rules={[
                  {
                    required: true,
                    message: "لطفاً روش پرداخت را انتخاب کنید.",
                  },
                ]}
              >
                <Select placeholder="روش پرداخت">
                  <Option value="online">آنلاین</Option>
                  {/* <Option value="on_site">در محل</Option> */}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="payment_status"
                label="وضعیت پرداخت"
                rules={[
                  {
                    required: true,
                    message: "لطفاً وضعیت پرداخت را انتخاب کنید.",
                  },
                ]}
              >
                <Select placeholder="وضعیت پرداخت">
                  <Option value="pending">در انتظار</Option>
                  <Option value="paid">پرداخت شده</Option>
                  <Option value="failed">ناموفق</Option>
                  <Option value="refunded">عودت داده شده</Option>
                  <Option value="canceled">لغو شده</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="deliver_status"
                label="وضعیت تحویل"
                rules={[
                  {
                    required: true,
                    message: "لطفاً وضعیت تحویل را انتخاب کنید.",
                  },
                ]}
              >
                <Select placeholder="وضعیت تحویل">
                  <Option value="pending">در انتظار</Option>
                  <Option value="processing">در حال پردازش</Option>
                  <Option value="shipped">ارسال شده</Option>
                  <Option value="delivered">تحویل داده شده</Option>
                  <Option value="canceled">لغو شده</Option>
                  <Option value="returned">مرجوع شده</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="price"
                label="قیمت"
                rules={[
                  {
                    required: true,
                    message: "لطفاً قیمت را وارد کنید.",
                  },
                ]}
              >
                <Input type="number" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}></Row>

          {/* فیلد انتخاب حامل */}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="carrier_id"
                label="انتخاب حامل"
                rules={[
                  {
                    required: true,
                    message: "لطفاً حامل را انتخاب کنید.",
                  },
                ]}
              >
                <Select
                  placeholder="انتخاب حامل"
                  loading={loadingCarriers}
                  onSearch={fetchCarriers}
                  showSearch
                  filterOption={false}
                  notFoundContent={
                    loadingCarriers ? (
                      <Spin size="small" />
                    ) : (
                      "نتیجه‌ای یافت نشد"
                    )
                  }
                >
                  {carriers.map((carrier) => (
                    <Option key={carrier.id} value={carrier.id}>
                      {carrier.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {/* سوییچ ارسال لینک پرداخت */}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="send_payment_link"
                label="ارسال لینک پرداخت"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          {/* دکمه افزودن محصول */}
          <Row gutter={16}>
            <Col span={24}>
              <Button
                className="mb-4"
                type="dashed"
                onClick={handleOpenProductModal}
              >
                افزودن محصول
              </Button>
            </Col>
          </Row>

          {/* جدول محصولات انتخاب شده */}
          <Row gutter={16}>
            <Col span={24}>
              <Table
                columns={productColumns}
                dataSource={selectedProductsList}
                rowKey="id"
                pagination={false}
              />
            </Col>
          </Row>

          <Row className="mt-4" gutter={16}>
            <Col span={24}>
              <Text strong style={{ fontSize: "16px" }}>
                مبلغ کل: {calculateTotalPrice().toLocaleString()} تومان
              </Text>
            </Col>
          </Row>

          <Form.Item>
            <Button
              className="mt-6"
              type="primary"
              htmlType="submit"
              loading={loading}
            >
              ایجاد سفارش
            </Button>
          </Form.Item>
        </Form>
      </Spin>

      {/* Modal ایجاد کاربر جدید */}
      <Modal
        title="ایجاد مشتری جدید"
        visible={isUserModalVisible}
        onCancel={() => setIsUserModalVisible(false)}
        footer={null}
      >
        <Form onFinish={handleCreateUser} layout="vertical">
          <Form.Item
            name="name"
            label="نام مشتری"
            rules={[
              {
                required: true,
                message: "لطفاً نام مشتری را وارد کنید.",
              },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="number"
            label="شماره تماس"
            rules={[
              {
                required: true,
                message: "لطفاً شماره تماس را وارد کنید.",
              },
            ]}
          >
            <Input />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={loading}>
            ایجاد مشتری
          </Button>
        </Form>
      </Modal>

      {/* Modal ایجاد آدرس جدید */}
      <Modal
        title="ایجاد آدرس جدید"
        visible={isAddressModalVisible}
        onCancel={() => setIsAddressModalVisible(false)}
        footer={null}
      >
        <Form layout="vertical" onFinish={handleCreateAddress}>
          <Form.Item
            name="name"
            label=" نام"
            rules={[
              {
                required: true,
                message: "لطفاً نام  را وارد کنید.",
              },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="post"
            label="کد پستی"
            rules={[
              {
                required: true,
                message: "لطفاً کد پستی را وارد کنید.",
              },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="state"
            label="استان"
            rules={[
              {
                required: true,
                message: "لطفاً استان را وارد کنید.",
              },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="city"
            label="شهر"
            rules={[
              {
                required: true,
                message: "لطفاً شهر را وارد کنید.",
              },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="plaque"
            label="پلاک"
            rules={[
              {
                required: true,
                message: "لطفاً پلاک را وارد کنید.",
              },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="number"
            label="شماره تماس"
            rules={[
              {
                required: true,
                message: "لطفاً شماره تماس را وارد کنید.",
              },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="address"
            label="آدرس کامل"
            rules={[
              {
                required: true,
                message: "لطفاً آدرس کامل را وارد کنید.",
              },
            ]}
          >
            <TextArea rows={4} />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={loading}>
            ایجاد مشتری
          </Button>
        </Form>
      </Modal>

      {/* Modal انتخاب محصول */}
      <Modal
        title="انتخاب محصول"
        visible={isProductModalVisible}
        onCancel={() => setIsProductModalVisible(false)}
        footer={null}
        width={800}
      >
        <Spin spinning={loadingProductDetails}>
          <Form
            form={productForm}
            onFinish={handleAddProduct}
            layout="vertical"
          >
            {/* فیلدهای مربوط به Modal محصول */}
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  name="product"
                  label="جستجو و انتخاب محصول"
                  rules={[
                    {
                      required: true,
                      message: "لطفاً محصول را انتخاب کنید.",
                    },
                  ]}
                >
                  <Select
                    showSearch
                    placeholder="جستجو و انتخاب محصول"
                    filterOption={false}
                    onSearch={fetchProducts}
                    notFoundContent={
                      loading ? <Spin size="small" /> : "نتیجه‌ای یافت نشد"
                    }
                    loading={loading}
                    onChange={(value) => fetchProductMiniDetails(value)}
                  >
                    {products.map((product) => (
                      <Option key={product.id} value={product.id}>
                        {product.title}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            {selectedProduct && (
              <>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="brand_id"
                      label="برند"
                      rules={[
                        {
                          required: true,
                          message: "لطفاً برند را انتخاب کنید.",
                        },
                      ]}
                    >
                      <Select
                        placeholder="انتخاب برند"
                        onChange={handleBrandChange}
                        loading={loadingProductDetails}
                      >
                        {brands.map((brand) => (
                          <Option key={brand.id} value={brand.id}>
                            {brand.name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="seller_id" label="فروشنده">
                      <Select
                        placeholder="انتخاب فروشنده"
                        onChange={handleSellerChange}
                        loading={loadingProductDetails}
                        disabled={!productForm.getFieldValue("brand_id")}
                      >
                        {sellers.map((seller) => (
                          <Option key={seller.id} value={seller.id}>
                            {seller.name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item name="guarantee_id" label="گارانتی">
                      <Select
                        placeholder="انتخاب گارانتی"
                        onChange={handleGuaranteeChange}
                        loading={loadingProductDetails}
                        disabled={!productForm.getFieldValue("seller_id")}
                      >
                        {guarantees.map((guarantee) => (
                          <Option key={guarantee.id} value={guarantee.id}>
                            {guarantee.name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="chassi_id"
                      label="شماره شاسی"
                      // rules={[
                      //   {
                      //     required: selectedProduct.need_shasi === 1,
                      //     message: "لطفاً شماره شاسی را وارد کنید.",
                      //   },
                      // ]}
                    >
                      <Input disabled={selectedProduct.need_shasi !== 1} />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="price"
                      label="قیمت"
                      rules={[
                        {
                          required: true,
                          message: "لطفاً قیمت را وارد کنید.",
                        },
                      ]}
                    >
                      <Input type="number" />
                    </Form.Item>
                    {productPrices.price && (
                      <div style={{ marginTop: "-20px", marginBottom: "16px" }}>
                        <Text type="secondary">
                          قیمت اصلی: {productPrices.price}
                        </Text>
                      </div>
                    )}
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="count"
                      label="تعداد"
                      rules={[
                        {
                          required: true,
                          message: "لطفاً تعداد را وارد کنید.",
                        },
                      ]}
                    >
                      <Input type="number" />
                    </Form.Item>
                    {selectedProduct && (
                      <div style={{ marginTop: "-20px", marginBottom: "16px" }}>
                        <Text type="secondary">
                          موجودی انبار: {selectedProduct?.count}
                        </Text>
                        <br />
                        {/* <Text type="secondary">
                          موجودی انبار ربانی: {selectedProduct.vipCount}
                        </Text> */}
                      </div>
                    )}
                  </Col>
                </Row>
              </>
            )}

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                disabled={!selectedProduct}
              >
                افزودن محصول
              </Button>
            </Form.Item>
          </Form>
        </Spin>
      </Modal>
    </Card>
  );
};

export default ManualOrderPage;
